import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ReadinessBadge } from "@/components/Badges";
import { useStartConsultation } from "@/components/Layout";
import { fmtDate } from "@/pages/Dashboard";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export const PageHead = ({ eyebrow, title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <p className="ig-rise font-mono-ig text-xs uppercase tracking-[0.3em] text-[#C5A059]">{eyebrow}</p>
      <h1 className="ig-rise ig-d1 mt-3 text-4xl font-semibold text-[#2C1810] sm:text-5xl">{title}</h1>
      {sub && <p className="ig-rise ig-d2 mt-3 max-w-2xl text-sm leading-relaxed text-[#6E5549] sm:text-base">{sub}</p>}
    </div>
    {children}
  </div>
);

export default function HistoryPage() {
  const qc = useQueryClient();
  const start = useStartConsultation();
  const { data = [], isLoading } = useQuery({ queryKey: ["consultations"], queryFn: async () => (await api.get("/consultations")).data });

  const [pending, setPending] = useState(null);
  const remove = async (id) => {
    try {
      await api.delete(`/consultations/${id}`);
      qc.invalidateQueries({ queryKey: ["consultations"] });
      toast.success("Consultation deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10 lg:py-14">
      <PageHead eyebrow="Archive" title="Consultations" sub="Every case, transcript and final recommendation — reopen any consultation to continue it.">
        <button onClick={start} data-testid="history-new-consultation-btn" className="rounded-full bg-[#2C1810] px-6 py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#3D2317]">New consultation</button>
      </PageHead>
      <div className="mt-10 space-y-3" data-testid="history-list">
        {data.map((c, i) => (
          <div key={c.id} className="ig-rise group flex items-center gap-4 rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] px-6 py-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#D99B90]" style={{ animationDelay: `${Math.min(i, 8) * 0.04}s` }}>
            <Link to={`/consult/${c.id}`} className="min-w-0 flex-1" data-testid={`history-item-${c.id}`}>
              <p className="truncate font-serif text-xl font-semibold text-[#2C1810] group-hover:text-[#9C6218]">{c.title}</p>
              <p className="mt-0.5 text-xs text-[#A88B7D]">{c.area === "lips" ? "Lip blush" : "Brows"} · Started {fmtDate(c.created_at)} · updated {fmtDate(c.updated_at)} · {c.message_count} messages</p>
            </Link>
            <span className="hidden sm:block"><ReadinessBadge status={c.readiness} testId={`history-readiness-${c.id}`} /></span>
            {c.readiness && <Link to={`/reports/${c.id}`} data-testid={`history-report-${c.id}`} className="rounded-full border border-[#E6DBCF] px-3 py-1.5 text-xs font-semibold text-[#2C1810] hover:bg-[#F6F1EA]">Report</Link>}
            <button onClick={() => setPending(c.id)} data-testid={`history-delete-${c.id}`} className="rounded-full p-2 text-[#A88B7D] transition-colors hover:bg-[#F9ECEC] hover:text-[#8C2A2A]" aria-label="Delete consultation">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {!isLoading && !data.length && <p className="text-sm italic text-[#A88B7D]" data-testid="history-empty">No consultations yet.</p>}
      </div>
      <ConfirmDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)} testId="history-delete-dialog"
        title="Delete this consultation?" description="The transcript and recommendation will be permanently removed."
        confirmLabel="Delete" onConfirm={() => remove(pending)} />
    </div>
  );
}
